import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography, Fade, makeStyles } from '@material-ui/core';
import { TransitionGroup } from 'react-transition-group';
import CustomModal from '../customModal';
import Scrollbars from '../scrollbars';
import VirtualizedList from '../virtualizedList';
import Tag from '../tag';
import Search from '../search/index';
import NoDataText from '../noDataText';
import VirtualTagCategory from './virtualTagCategory';
import { isArrayEmpty, getObjectToNumberArray } from '../../../utility/helpers';
import { sortAlphabetically } from '../../../utility/uiUtils';
import { filterTags } from '../../../utility/tagUtils';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  description: {
    paddingBottom: spacing(4),
  },
  search: {
    width: '100%',
    maxWidth: '100%',
    paddingBottom: spacing(4),
    borderBottom: `1px solid ${primary.bluish8}`,
  },
  selectedTagsLabel: {
    fontFamily: 'ProximaNova-Bold',
    lineHeight: '20px',
    marginTop: spacing(4),
  },
  selectedTagsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: spacing(4),
    minHeight: 20,
    maxHeight: 112,
  },
  divider: {
    boxSizing: 'border-box',
    borderTop: `1px solid ${primary.bluish7}`,
    marginTop: spacing(2),
    paddingTop: spacing(4),
    width: '100%',
  },
  selectedTagsViewport: {
    maxHeight: 112,
  },
  tagsWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    minHeight: 0,
  },
  tagsScrollWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: 200,
    minHeight: 0,
  },
  scrollYNew: {
    right: -19,
  },
}));

const ManageUserTagsDialog = ({
  translations,
  isOpened,
  categories,
  selectedTags = [],
  onCancel,
  onSave,
  userAvatars = undefined,
}) => {
  const classes = useStyles();
  const [tags, setTags] = useState(selectedTags);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (isOpened) {
      setTags(selectedTags);
    }
  }, [isOpened, selectedTags]);

  const filteredCategories = useMemo(
    () => filterTags(categories, searchValue, tags),
    [categories, searchValue, tags]
  );

  const handleReset = () => {
    setSearchValue('');
  };

  const handleClose = () => {
    onCancel();
    handleReset();
  };

  const handleSave = () => {
    handleReset();
    return onSave({ tags: getObjectToNumberArray(tags) });
  };

  const onSelectTag = useCallback(
    (tag, category) =>
      setTags(prevTags =>
        sortAlphabetically([...prevTags, { ...tag, color: category.color }])
      ),
    []
  );

  const onDeselectTag = tag =>
    setTags(prevTags =>
      prevTags.filter(previousTag => previousTag.id !== tag.id)
    );

  const handleSearch = value => setSearchValue(value);

  const getVirtualTagCategorySize = useCallback(() => 64, []);

  const renderTagsCategory = useCallback(
    category => (
      <VirtualTagCategory
        translations={translations}
        category={category}
        onSelectTag={onSelectTag}
      />
    ),
    [translations, onSelectTag]
  );

  return (
    <CustomModal
      title={translations.title}
      confirmButtonLabel={translations.save}
      closeButtonLabel={translations.cancel}
      isOpened={isOpened}
      onClose={handleClose}
      onConfirm={handleSave}
      isMedium
    >
      {userAvatars || (
        <Typography className={classes.description}>
          {translations.description}
        </Typography>
      )}
      <Search
        className={classes.search}
        label={translations.search.label}
        placeholder={translations.search.placeholder}
        value={searchValue}
        onChange={handleSearch}
      />
      <Typography className={classes.selectedTagsLabel}>
        {translations.selectedTags}
      </Typography>
      <div className={classes.selectedTagsWrapper}>
        {!isArrayEmpty(tags) ? (
          <Scrollbars
            viewportClass={classes.selectedTagsViewport}
            verticalScrollClass={classes.scrollYNew}
          >
            <TransitionGroup className={classes.tagsWrapper}>
              {tags.map(tag => (
                <Fade key={`tag_item_${tag.id}`} in>
                  <div>
                    <Tag
                      className={classes.tag}
                      tag={tag}
                      color={tag?.color || tag?.category?.color}
                      onTagSelect={onDeselectTag}
                      hasRemove
                      isSelected
                      isSelectable
                    />
                  </div>
                </Fade>
              ))}
            </TransitionGroup>
          </Scrollbars>
        ) : (
          <NoDataText text={translations.noSelectedTags} />
        )}
      </div>
      <div className={classes.divider} />
      <div className={classes.tagsScrollWrapper}>
        {!isArrayEmpty(filteredCategories) ? (
          <VirtualizedList
            verticalScrollClass={classes.scrollYNew}
            items={filteredCategories}
            count={filteredCategories?.length ?? 0}
            gap={8}
            estimateSize={getVirtualTagCategorySize}
            renderItem={renderTagsCategory}
            isDynamicSize
          />
        ) : (
          <NoDataText text={translations.noResults} />
        )}
      </div>
    </CustomModal>
  );
};

ManageUserTagsDialog.propTypes = {
  translations: PropTypes.object.isRequired,
  isOpened: PropTypes.bool.isRequired,
  categories: PropTypes.arrayOf(PropTypes.object).isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  selectedTags: PropTypes.array,
  userAvatars: PropTypes.object,
};

export default ManageUserTagsDialog;
